'use client';

import { useLocale } from 'next-intl';
import { ChangeEvent, KeyboardEvent, useCallback, useState } from 'react';
import { subscribeMail } from '@/services/salesforece-mail';
import { generateSubscribeDataString } from '@/utils/landing-attribution';
import { sendSentryError } from '@lib/sentry';
import { MessageType } from './subscribe-message';

const emailReg = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;

const validateEmail = (
  value: string,
): { error: 'required' | 'invalid-email' | null } => {
  if (value === '') return { error: 'required' };
  if (!emailReg.test(value)) return { error: 'invalid-email' };
  return { error: null };
};

export const useEmailInput = (
  setMessageType: (messageType: MessageType) => void,
) => {
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);

  const onEmailChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
      const { error } = validateEmail(e.target.value);

      if (!error) {
        setMessageType(null);
        setIsEmailValid(true);
      } else {
        setMessageType(error);
        setIsEmailValid(false);
      }
    },
    [setMessageType],
  );

  return {
    email,
    isEmailValid,
    onEmailChange,
  };
};

export const useFormFocusState = () => {
  const [touched, setTouched] = useState(false);
  const [focused, setFocused] = useState(false);

  const onFormBlur = useCallback(() => {
    setFocused(false);
  }, [setFocused]);

  const onFormFocus = useCallback(() => {
    if (!touched) setTouched(true);
    setFocused(true);
  }, [touched, setTouched, setFocused]);

  return {
    touched,
    focused,
    onFormBlur,
    onFormFocus,
  };
};

export const useSubscribeSubmit = (
  setMessageType: (messageType: MessageType) => void,
  email: string,
  onSuccess?: () => void,
) => {
  const locale = useLocale();

  const [isLoading, setIsLoading] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const onFormSubmit = useCallback(async () => {
    if (validateEmail(email).error !== null || isLoading) return;

    const isSubmittedEmail = submittedEmail === email;
    if (isSubmittedEmail) {
      setMessageType('already-subscribed');
      return;
    }

    setIsLoading(true);

    try {
      const data = generateSubscribeDataString(email);
      await subscribeMail(data, locale);
      setSubmittedEmail(email);
      onSuccess?.();

      setMessageType('fetch-success');
    } catch (error) {
      sendSentryError({
        error,
        source: 'footer-subscribe',
      });
      setMessageType('fetch-error');
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, submittedEmail, email, setMessageType, locale, onSuccess]);

  const onSubmitOnEnter = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onFormSubmit();
      }
    },
    [onFormSubmit],
  );

  return {
    onFormSubmit,
    onSubmitOnEnter,
    isLoading,
  };
};
